/**
 * Single source for user timezone and formatting datetimes in a given timezone.
 * Display timezone priority: order's availability_timezone (location customer/worker selected) then
 * the city-derived timezone from LocationContext (localStorage 'userTimezone') then browser/OS timezone.
 */

const FALLBACK_TIMEZONE = 'America/Toronto';

/**
 * Get the user's effective timezone.
 * Priority: city-derived timezone stored in localStorage ('userTimezone') → browser/OS timezone.
 * This ensures display always matches the user's selected city, not just their device locale.
 */
export function getUserTimezone(): string {
  try {
    const cityTz = localStorage.getItem('userTimezone');
    if (cityTz) return cityTz;
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIMEZONE;
  } catch {
    return FALLBACK_TIMEZONE;
  }
}

/** Returns the raw browser/OS timezone regardless of selected city. */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIMEZONE;
  } catch {
    return FALLBACK_TIMEZONE;
  }
}

export type FormatInTimeZoneOptions = {
  /** 'date' = date only, 'datetime' = date + time (default) */
  mode?: 'date' | 'datetime';
  /** e.g. 'short' -> "Feb 19, 2026" / "2:30 PM" */
  dateStyle?: 'short' | 'medium' | 'long' | 'full';
  timeStyle?: 'short' | 'medium' | 'long' | 'full';
  /** Show timezone abbreviation (e.g. EST) */
  includeTz?: boolean;
};

/**
 * Format an ISO 8601 datetime string for display in a specific timezone.
 * Use for any datetime from the API (always UTC ISO). Pass order.availability_timezone when in order context, else getUserTimezone().
 */
export function formatInTimeZone(
  isoString: string | null | undefined,
  timezone: string,
  options: FormatInTimeZoneOptions = {}
): string {
  if (isoString == null || isoString === '') return '';
  try {
    // Date-only strings (YYYY-MM-DD) must be parsed as LOCAL noon to avoid the JS UTC-midnight
    // gotcha where new Date('2024-01-15') = UTC midnight → shows Jan 14 in UTC-5 timezones.
    // Using 'YYYY/MM/DD' forces JS to treat it as local time (not UTC).
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(isoString);
    const date = isDateOnly
      ? new Date(isoString.replace(/-/g, '/') + ' 12:00:00') // local noon, safe for any ±12h timezone
      : new Date(isoString);
    if (Number.isNaN(date.getTime())) return isoString;
    const tz = timezone || getUserTimezone();
    const { mode = 'datetime', dateStyle = 'medium', timeStyle = 'short', includeTz = false } = options;

    // dateStyle/timeStyle cannot be combined with timeZoneName (throws RangeError in spec-compliant engines).
    const formatOptions: Intl.DateTimeFormatOptions =
      mode === 'date' || isDateOnly
        ? {
            timeZone: tz,
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }
        : includeTz
          ? {
              timeZone: tz,
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              timeZoneName: 'short',
            }
          : {
              timeZone: tz,
              dateStyle,
              timeStyle,
            };

    return new Intl.DateTimeFormat(undefined, formatOptions).format(date);
  } catch {
    return isoString;
  }
}

/**
 * Format date only (no time) in the given timezone. Use for date-only API fields when showing in order context.
 */
export function formatDateInTimeZone(
  dateString: string | null | undefined,
  timezone: string
): string {
  if (dateString == null || dateString === '') return '';
  try {
    // Use local noon to avoid the UTC-midnight shift on date-only strings
    const date = new Date(dateString.replace(/-/g, '/') + ' 12:00:00');
    if (Number.isNaN(date.getTime())) return dateString;
    const tz = timezone || getUserTimezone();
    return new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  } catch {
    return dateString ?? '';
  }
}

/**
 * Format time only (no date) in the given timezone.
 */
export function formatTimeInTimeZone(isoString: string | null | undefined, timezone: string): string {
  if (isoString == null || isoString === '') return '';
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '';
    const tz = timezone || getUserTimezone();
    return new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return '';
  }
}

/**
 * Resolve display timezone for rendering dates & times.
 * Always returns the user's currently selected city timezone (via getUserTimezone) so that
 * switching city immediately updates all displayed times across the app.
 * The order's stored availability_timezone is kept for backend scheduling only.
 */
export function getDisplayTimezone(_order?: { availability_timezone?: string | null } | null): string {
  return getUserTimezone();
}

/**
 * Today's date in the user's timezone as YYYY-MM-DD (single source of truth for export filenames etc.).
 */
export function getTodayDateString(): string {
  const tz = getUserTimezone();
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const y = parts.find((p) => p.type === 'year')?.value ?? '';
  const m = parts.find((p) => p.type === 'month')?.value ?? '';
  const d = parts.find((p) => p.type === 'day')?.value ?? '';
  return `${y}-${m}-${d}`;
}
